import React, { useState, useEffect } from "react";
import { useBar } from "../../../../context/BarContext";
import { useUserRole } from "../../../../context/UserRoleContext";
import {
  searchUserByEmail,
  sendBarAdminInvite,
} from "../../../../services/api";

const InviteAdminForm = () => {
  const { userBars } = useBar();
  const { userData } = useUserRole();
  const currentUserId = userData?.user_id ?? null;

  const [selectedBars, setSelectedBars] = useState([]);
  const [email, setEmail] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [canInvite, setCanInvite] = useState(false);
  const [inviteStatus, setInviteStatus] = useState("none");
  const [statusMessage, setStatusMessage] = useState(null);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Check edge cases when searchResult changes
  useEffect(() => {
    if (!searchResult) {
      setCanInvite(false);
      setInviteStatus("none");
      setStatusMessage(null);
      return;
    }

    if (searchResult.user_id === currentUserId) {
      setCanInvite(false);
      setInviteStatus("self");
      setStatusMessage("You cannot invite yourself.");
      return;
    }

    if (searchResult.role !== "bar_admin") {
      setCanInvite(false);
      setInviteStatus("wrong_role");
      setStatusMessage(
        "Bar admin invites can only be sent to users with bar_admin role."
      );
      return;
    }

    setCanInvite(true);
    setInviteStatus("valid");
    setStatusMessage(null);
  }, [searchResult, currentUserId]);

  const handleBarToggle = (barId) => {
    setSelectedBars((prev) =>
      prev.includes(barId)
        ? prev.filter((id) => id !== barId)
        : [...prev, barId]
    );
  };

  const handleSelectAll = () => {
    if (userBars && userBars.length > 0) {
      setSelectedBars(userBars.map((b) => b.bar_id));
    }
  };

  const handleDeselectAll = () => {
    setSelectedBars([]);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setSearchError(null);
    setSuccess(null);
    setSearchResult(null);
    setStatusMessage(null);
    setCanInvite(false);
    setInviteStatus("none");

    if (!email.trim()) {
      setSearchError("Please enter an email address.");
      return;
    }

    if (!validateEmail(email.trim())) {
      setSearchError("Please enter a valid email address.");
      return;
    }

    try {
      setIsSearching(true);
      const response = await searchUserByEmail(email.trim());
      if (response.success) {
        setSearchResult(response.user);
      } else {
        setSearchError(response.error || "User not found");
      }
    } catch (err) {
      setSearchResult(null);
      setSearchError(err.message || "User not found");
    } finally {
      setIsSearching(false);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!searchResult?.user_id) {
      setError("Search for a user before sending an invite.");
      return;
    }

    if (!canInvite) {
      setError("Cannot send invite. Please check the status message.");
      return;
    }

    if (selectedBars.length === 0) {
      setError("Please select at least one bar.");
      return;
    }

    setIsSubmitting(true);

    try {
      const data = await sendBarAdminInvite(
        selectedBars,
        email.trim().toLowerCase()
      );

      if (data.success) {
        const count = data.inviteIds?.length ?? 0;
        let successMsg = `Invites sent successfully to ${count} bar(s).`;
        if (data.skipped) {
          const { associated = [], existingInvites = [] } = data.skipped;
          const skippedCount = new Set([...associated, ...existingInvites])
            .size;
          if (skippedCount > 0) {
            successMsg += ` (User is already admin or has pending invite for ${skippedCount} other bar(s).)`;
          }
        }
        setSuccess(successMsg);
        setEmail("");
        setSearchResult(null);
        setSelectedBars([]);
        setCanInvite(false);
        setInviteStatus("none");
        setTimeout(() => setSuccess(null), 5000);
        window.dispatchEvent(new CustomEvent("adminInvitesUpdated"));
      } else {
        setError(data.error || "Failed to send invite");
      }
    } catch (err) {
      setError(err.message || "Failed to send invite");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setEmail("");
    setSearchResult(null);
    setSearchError(null);
    setStatusMessage(null);
    setCanInvite(false);
    setInviteStatus("none");
  };

  const availableBars = userBars || [];
  const allSelected =
    availableBars.length > 0 && selectedBars.length === availableBars.length;

  return (
    <div className="invite-admin-form-section">
      <div className="form-header">
        <h3>Invite New Bar Admin</h3>
        <p>Search for an existing user and send an in-app invitation.</p>
      </div>

      {error && (
        <div
          className="alert-error"
          style={{
            marginBottom: "20px",
            padding: "10px",
            backgroundColor: "#fee",
            color: "#c00",
            border: "1px solid #c00",
            borderRadius: "5px",
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          className="alert-success"
          style={{
            marginBottom: "20px",
            padding: "10px",
            backgroundColor: "#d1f2eb",
            color: "#065f46",
            border: "1px solid #065f46",
            borderRadius: "5px",
          }}
        >
          {success}
        </div>
      )}

      <form onSubmit={handleSearch} className="invite-form">
        <div className="input-group">
          <label className="input-label">Email Address *</label>
          <div
            style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}
          >
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setSearchResult(null);
                setSearchError(null);
              }}
              className="settings-input"
              placeholder="admin@example.com"
              style={{ flex: 1 }}
              disabled={!!searchResult}
            />
            <button
              type="submit"
              className="submit-btn"
              disabled={isSearching || !email.trim()}
            >
              {isSearching ? "Searching..." : "Search"}
            </button>
            {searchResult && (
              <button
                type="button"
                className="bar-select-btn"
                onClick={handleReset}
              >
                Change
              </button>
            )}
          </div>
          <p className="input-hint">
            Search for a user by email. They must already have an account with
            bar_admin role. They will receive an in-app notification.
          </p>
        </div>
      </form>

      {searchError && (
        <p
          className="groups-modal-error"
          role="alert"
          style={{ marginBottom: "15px", color: "#c00" }}
        >
          {searchError}
        </p>
      )}

      {searchResult && (
        <div className="invite-admin-user-found">
          <h4 className="invite-admin-user-found-title">User Found</h4>
          <div className="invite-admin-user-found-details">
            <div>
              <span className="invite-admin-user-found-label">Name:</span>{" "}
              {searchResult.name || "N/A"}
            </div>
            <div>
              <span className="invite-admin-user-found-label">Email:</span>{" "}
              {searchResult.email}
            </div>
            <div>
              <span className="invite-admin-user-found-label">Role:</span>{" "}
              {searchResult.role}
            </div>
          </div>
          {statusMessage && (
            <p
              role="alert"
              className={`invite-admin-user-found-status invite-admin-user-found-status--${inviteStatus}`}
            >
              {statusMessage}
            </p>
          )}
        </div>
      )}

      {searchResult && (
        <form onSubmit={handleSendInvite}>
          <div className="input-group">
            <label className="input-label">Select Bars *</label>
            {availableBars.length === 0 ? (
              <p className="input-hint" style={{ color: "#999" }}>
                No bars available. Please create a bar first.
              </p>
            ) : (
              <>
                <div
                  className="bar-select-actions"
                  style={{
                    marginBottom: "10px",
                    display: "flex",
                    gap: "10px",
                  }}
                >
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    disabled={allSelected}
                    className="bar-select-btn"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={handleDeselectAll}
                    disabled={selectedBars.length === 0}
                    className="bar-select-btn"
                  >
                    Deselect All
                  </button>
                </div>
                <div className="bar-select-list">
                  {availableBars.map((bar) => {
                    const isSelected = selectedBars.includes(bar.bar_id);
                    return (
                      <label
                        key={bar.bar_id}
                        className={`bar-select-item ${
                          isSelected ? "selected" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleBarToggle(bar.bar_id)}
                          className="bar-select-checkbox"
                        />
                        <div className="bar-select-info">
                          <div className="bar-select-name">{bar.name}</div>
                          {bar.address && (
                            <div className="bar-select-address">
                              {bar.address}
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="submit-btn"
              disabled={!canInvite || isSubmitting || selectedBars.length === 0}
            >
              {isSubmitting
                ? "Sending..."
                : `Send Invite${selectedBars.length > 1 ? "s" : ""} (${
                    selectedBars.length
                  })`}
            </button>
          </div>
        </form>
      )}

      <div className="invite-info-box">
        <h4>Admin Permissions</h4>
        <p>Bar admins will be able to:</p>
        <ul>
          <li>View and manage show bookings</li>
          <li>Manage artist relationships</li>
          <li>View financial reports</li>
          <li>Access bar calendar</li>
        </ul>
        <p>
          <strong>Note:</strong> They cannot add/remove other admins or delete
          the bar.
        </p>
      </div>
    </div>
  );
};

export default InviteAdminForm;
